export interface NdjsonEventLine {
  kind: "event"
  json: unknown
}

export interface NdjsonMalformedLine {
  kind: "malformed"
  line: string
  error: unknown
}

export interface NdjsonDone {
  kind: "done"
}

export interface NdjsonIdle {
  kind: "idle"
}

export interface NdjsonReadFailed {
  kind: "read_failed"
  error: unknown
}

export type NdjsonOutcome = NdjsonEventLine | NdjsonMalformedLine | NdjsonDone | NdjsonIdle | NdjsonReadFailed

export const CHAT_STREAM_IDLE_TIMEOUT_MS = 90_000

export interface ReadNdjsonStreamOptions {
  idleTimeoutMs: number
}

type IdleAwareReadResult = ReadableStreamReadResult<Uint8Array> | "idle"

async function readWithIdleTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  idleTimeoutMs: number,
): Promise<IdleAwareReadResult> {
  let idleTimer: ReturnType<typeof setTimeout> | undefined
  const idle = new Promise<"idle">((resolve) => {
    idleTimer = setTimeout(() => resolve("idle"), idleTimeoutMs)
  })
  try {
    return await Promise.race([reader.read(), idle])
  } finally {
    clearTimeout(idleTimer)
  }
}

function parseLine(line: string): NdjsonEventLine | NdjsonMalformedLine {
  try {
    return { kind: "event", json: JSON.parse(line) }
  } catch (error) {
    return { kind: "malformed", line, error }
  }
}

function* drainCompleteLines(buffer: { text: string }): Generator<NdjsonEventLine | NdjsonMalformedLine> {
  let newlineIndex = buffer.text.indexOf("\n")
  while (newlineIndex !== -1) {
    const line = buffer.text.slice(0, newlineIndex)
    buffer.text = buffer.text.slice(newlineIndex + 1)
    if (line.trim()) yield parseLine(line)
    newlineIndex = buffer.text.indexOf("\n")
  }
}

export async function* readNdjsonStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options: ReadNdjsonStreamOptions,
): AsyncGenerator<NdjsonOutcome> {
  const decoder = new TextDecoder()
  const buffer = { text: "" }

  while (true) {
    let readResult: IdleAwareReadResult
    try {
      readResult = await readWithIdleTimeout(reader, options.idleTimeoutMs)
    } catch (error) {
      yield { kind: "read_failed", error }
      return
    }

    if (readResult === "idle") {
      yield { kind: "idle" }
      return
    }

    if (readResult.done) {
      buffer.text += decoder.decode()
      yield* drainCompleteLines(buffer)
      if (buffer.text.trim()) yield parseLine(buffer.text)
      yield { kind: "done" }
      return
    }

    buffer.text += decoder.decode(readResult.value, { stream: true })
    yield* drainCompleteLines(buffer)
  }
}
